export interface ItemReference {
  id: string
  type: string
}

export interface CommerceLayerError {
  code: string
  detail: string
  meta: Record<string, string>
  source: Record<string, string>
  status: string
  title: string
}

export interface Relationships {
  shipments?: {
    data: ItemReference[]
  }
  shipment?: {
    data: ItemReference
  }
  shipping_zone?: {
    data: ItemReference
  }
  shipping_category?: {
    data: ItemReference
  }
  billing_address?: {
    data: ItemReference
  }
  shipping_address?: {
    data: ItemReference
  }
  delivery_lead_time_for_shipment?: {
    data: ItemReference
  }
  delivery_lead_times?: {
    data: ItemReference[]
  }
  available_shipping_methods?: {
    data: ItemReference[]
  }
  price_list?: {
    data: ItemReference
  }
  shipping_method?: {
    data: ItemReference
  }
  payment_source: {
    data: ItemReference
  }
}

export interface Item<T> {
  id: string
  type: string
  attributes: T
  relationships?: Relationships
}

export interface PaymentSourceDetails {
  payment_method_details: {
    brand: string
    checks: Record<string, any>
    country: string
    exp_month: number
    exp_year: number
    fingerprint: string
    funding: string
    generated_from: string
    last4: string
    three_d_secure_usage: { supported: boolean }
    wallet: string
  }
  payment_method_type: string
  type: string
}

export interface OrderAttributes {
  country_code: string
  coupon_code: string
  created_at: string
  currency_code: string
  customer_email: string
  discount_amount_cents: number
  discount_amount_float: number
  discount_tax_amount_cents: number
  discount_tax_amount_float: number
  discount_taxable_amount_cents: number
  discount_taxable_amount_float: number
  editable: boolean
  formatted_discount_amount: string
  formatted_payment_method_amount: string
  formatted_shipping_amount: string
  formatted_subtotal_amount: string
  formatted_subtotal_tax_amount: string
  formatted_total_amount: string
  formatted_total_amount_with_taxes: string
  formatted_total_tax_amount: string
  guest: boolean
  language_code: string
  line_item_options_count: number
  shipments_count: number
  number: number
  payment_source_details: PaymentSourceDetails
  payment_status: string
  payment_updated_at: string
  placeable: boolean
  placed_at: string
  shipping_amount_cents: number
  shipping_amount_float: number
  shipping_taxable_amount_cents: number
  shipping_taxable_amount_float: number
  skus_count: number
  status: string
  subtotal_amount_cents: number
  subtotal_amount_float: number
  subtotal_tax_amount_cents: number
  subtotal_tax_amount_float: number
  subtotal_taxable_amount_cents: number
  subtotal_taxable_amount_float: number
  tax_rate: number | null
  tax_included: boolean
  total_amount_cents: number
  total_amount_float: number
  total_amount_with_taxes_cents: number
  total_amount_with_taxes_float: number
  total_tax_amount_cents: number
  total_tax_amount_float: number
  total_taxable_amount_cents: number
  total_taxable_amount_float: number
  updated_at: string
  metadata: {
    paymentMethod: {
      id: string
    }
  }
}

export interface AddressAttributes {
  business: boolean
  first_name: string
  last_name: string
  company: string
  full_name?: string
  line_1: string
  line_2: string
  city: string
  zip_code: string
  state_code: string
  country_code: string
  phone: string
  full_address?: string
  billing_info?: string
  name?: string
  notes?: string
  provider_name?: string
  created_at?: string
  updated_at?: string
  metadata: {
    street: string
    homeNo: string
    localNo: string
  }
}

export interface ShipmentAttributes {
  cost_amount_cents: number
  cost_amount_float: number
  created_at: string
  currency_code: string
  formatted_cost_amount: string
  number: string
  skus_count: number
  status: string
  updated_at: string
  metadata: {
    delivery_notes?: string
  }
}

export interface ShipmentMethodAttributes {
  name: string
  price_amount_cents: number
  free_over_amount_cents: string
  reference: string
  metadata: {
    imageUrl: string
    maxKg: string
  }
  market: string
  shipping_zone: number
  shipping_category: string
  disabled_at: string
  currency_code: string
  price_amount_float: number
  formatted_price_amount: string
  free_over_amount_float: number
  formatted_free_over_amount: number
  price_amount_for_shipment_cents: number
  price_amount_for_shipment_float: number
  formatted_price_amount_for_shipment: string
  updated_at: string
  id: string
}

export interface LineItemAttributes {
  created_at: string
  currency_code: string
  formatted_options_amount: string
  formatted_total_amount: string
  formatted_unit_amount: string
  image_url: string
  item_type: string
  name: string
  options_amount_cents: number
  options_amount_float: number
  quantity: number
  sku_code: string
  tax_rate: number
  total_amount_cents: number
  total_amount_float: number
  unit_amount_cents: number
  unit_amount_float: number
  updated_at: string
}

export interface PaymentAttributes {
  created_at: string
  disabled_at: string
  formatted_price_amount: string
  name: string
  payment_source_type: string
  price_amount_cents: number
  price_amount_float: number
  updated_at: string
  id: string
  metadata: {
    imageUrl: string
  }
}

export interface PriceAttributes {
  amount_cents: number
  amount_float: number
  compare_at_amount_cents: number
  compare_at_amount_float: number
  created_at: string
  currency_code: string
  formatted_amount: string
  formatted_compare_at_amount: string
  reference: string
  sku_code: string
  updated_at: string
}

type CurrencyCode = "USD" | "EUR" | "PL"

export interface PriceListAttributes {
  name: string
  currency_code: CurrencyCode
  tax_included: boolean
}

export interface PackageAttributes {
  name: string
  code?: string
  length: string
  width: string
  height: string
  unit_of_length: string
}

export interface CreditCardAttributes {
  first_name: string
  last_name: string
  number: string
  month: string
  year: string
  verification_value: string
}

export interface CustomerAttributes {
  email: string
}

export interface MarketAttributes {
  name: string
  number: number
  currency: string
  reference?: string
  metadata?: {
    type: string
  }
}

export interface DeliveryLeadTimeAttributes {
  min_hours: number
  max_hours: number
  min_days: number
  max_days: number
}

export enum ReferenceType {
  Pure = "pure",
  Moment = "moment",
  Bell = "bell",
  Harmony = "harmony",
  PureCover = "pureCover",
}

export interface SKUAttributes {
  code: string
  name: string
  description: string
  image_url: string
  tag_names: string
  pieces_per_pack: string
  weight: string
  unit_of_weight: string
  created_at: string
  updated_at: string
  reference: ReferenceType
  reference_origin: string
  metadata?: {
    weight: string
    preorder: boolean
  }
}

export interface ShippingZoneAttributes {
  name: string
  country_code_regex: string
  not_country_code_regex: string
  state_code_regex: string
  not_state_code_regex: string
  zip_code_regex: string
  not_zip_code_regex: string
  reference: string
  reference_origin: string | null
}

export interface StripePaymentAttributes {
  client_secret: string
  options?: {
    customer: string
    payment_method: string
  }
}

export interface StripeResponseAttributes {
  amount: number
  canceled_at: string
  cancellation_reason: string
  capture_method: string
  client_secret: string
  confirmation_method: string
  created: number
  currency: string
  description: string
  id: string
  last_payment_error: string
  livemode: boolean
  next_action: string
  object: string
  payment_method: string
  payment_method_types: string[]
  receipt_email: string
  setup_future_usage: boolean
  shipping: string
  source: string
  status: string
}

export interface StockItemAttributes {
  quantity: number
  reference: string
  reference_origin: string
  sku_code: string
  created_at: string
  updated_at: string
}

export interface PayPalResponseAttributes {
  return_url: string
  cancel_url: string
  note_to_payer: string
  paypal_payer_id: string
  name: string
  paypal_id: string
  status: string
  approval_url: string
  created_at: string
  updated_at: string
  reference: string
  reference_origin: string
}

export type Attributes =
  | OrderAttributes
  | AddressAttributes
  | ShipmentAttributes
  | ShipmentMethodAttributes
  | LineItemAttributes
  | PaymentAttributes
  | CreditCardAttributes
  | PriceAttributes
  | PriceListAttributes
  | CustomerAttributes
  | MarketAttributes
  | DeliveryLeadTimeAttributes
  | ShippingZoneAttributes
  | StripePaymentAttributes
  | PackageAttributes
  | PayPalResponseAttributes
  | SKUAttributes

export interface CommerceObject<T> {
  data: Item<T>
  included?: Item<Attributes>[]
  error?: any
}

export interface CommerceArray<T> {
  data: Item<T>[]
  included?: Item<Attributes>[]
}

export enum LineItemType {
  SKU = "skus",
}

export enum AttributeType {
  Order = "orders",
  Payment = "payment_methods",
  Address = "addresses",
  Shipment = "shipments",
  ShipmentMethod = "shipping_methods",
  DeliveryLeadTime = "delivery_lead_times",
  LineItem = "line_items",
  Market = "markets",
  PriceList = "price_lists",
  Price = "prices",
  StripePayment = "stripe_payments",
  WireTransfer = "wire_transfers",
  ShipmentLineItem = "shipment_line_items",
}

export type Included = Item<Attributes>[]
export type Price = Item<PriceAttributes>
export type Product = Item<SKUAttributes>
export type Order = CommerceObject<OrderAttributes>
export type Shipment = CommerceObject<ShipmentAttributes>
export type ShipmentMethods = CommerceArray<ShipmentMethodAttributes>
export type PaymentMethods = CommerceArray<PaymentAttributes>
export type Prices = CommerceArray<PriceAttributes>
export type PriceList = Item<PriceListAttributes>
export type Market = Item<MarketAttributes>
export type ShippingZone = Item<ShippingZoneAttributes>
export type StripePayment = CommerceObject<StripePaymentAttributes>
export type PayPalPayment = CommerceObject<PayPalResponseAttributes>
export type PaymentMethod = Item<PaymentAttributes>
export type DeliveryLeadTime = Item<DeliveryLeadTimeAttributes>
export type ShipmentMethod = Item<ShipmentMethodAttributes>
